import { useSyncExternalStore } from "react";
import {
  audioBitrateOptions,
  audioProfiles,
} from "../ui/conference-settings-modal/conference-settings.constants";

const STORAGE_KEY = "conference-settings-v1";

const getDefaultState = () => ({
  selectedProfile: "vocals",
  selectedAudioBitrate: "highest",
  selectedAudioOutputDeviceId: "default",
  audioSettings: { ...audioProfiles.vocals.settings },
  deviceLevels: {
    microphone: 100,
    speaker: 100,
  },
});

const isClient = () => typeof window !== "undefined";

const parseStoredState = () => {
  if (!isClient()) {
    return getDefaultState();
  }

  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);

    if (!rawValue) {
      return getDefaultState();
    }

    const parsed = JSON.parse(rawValue);
    const defaults = getDefaultState();

    const selectedProfile = audioProfiles[parsed?.selectedProfile]
      ? parsed.selectedProfile
      : defaults.selectedProfile;

    return {
      selectedProfile,
      selectedAudioBitrate:
        audioBitrateOptions.some((option) => option.value === parsed?.selectedAudioBitrate)
          ? parsed.selectedAudioBitrate
          : defaults.selectedAudioBitrate,
      selectedAudioOutputDeviceId:
        typeof parsed?.selectedAudioOutputDeviceId === "string" &&
        parsed.selectedAudioOutputDeviceId.length
          ? parsed.selectedAudioOutputDeviceId
          : defaults.selectedAudioOutputDeviceId,
      audioSettings: {
        ...defaults.audioSettings,
        ...(parsed?.audioSettings || {}),
      },

      deviceLevels: {
        ...defaults.deviceLevels,
        ...(parsed?.deviceLevels || {}),
      },
    };
  } catch {
    return getDefaultState();
  }
};

let state = parseStoredState();
const listeners = new Set();

const emit = () => {
  listeners.forEach((listener) => listener());
};

const persist = () => {
  if (!isClient()) {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

const setState = (nextState) => {
  state = nextState;
  persist();
  emit();
};

const subscribe = (listener) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => state;

const setSelectedProfile = (selectedProfile) => {
  setState({
    ...state,
    selectedProfile,
  });
};

const setSelectedAudioBitrate = (selectedAudioBitrate) => {
  setState({
    ...state,
    selectedAudioBitrate,
  });
};

const setSelectedAudioOutputDeviceId = (selectedAudioOutputDeviceId) => {
  setState({
    ...state,
    selectedAudioOutputDeviceId,
  });
};

const setAudioSettings = (nextAudioSettings) => {
  const audioSettings =
    typeof nextAudioSettings === "function"
      ? nextAudioSettings(state.audioSettings)
      : nextAudioSettings;

  setState({
    ...state,
    audioSettings,
  });
};

const setDeviceLevels = (nextDeviceLevels) => {
  const deviceLevels =
    typeof nextDeviceLevels === "function" ? nextDeviceLevels(state.deviceLevels) : nextDeviceLevels;

  setState({
    ...state,
    deviceLevels,
  });
};

export const useConferenceSettingsStore = () => {
  const conferenceSettings = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return {
    ...conferenceSettings,
    setSelectedProfile,
    setSelectedAudioBitrate,
    setSelectedAudioOutputDeviceId,
    setAudioSettings,
    setDeviceLevels,
  };
};
